import type { Integration as SharedIntegration } from '@gofranz/common-components';
import {
  IntegrationForm,
  type IntegrationFormSubmitPayload,
  useLanguageAwareRouting,
} from '@gofranz/common-components';
import { Container, Stack } from '@mantine/core';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../../components/Common/PageHeader';
import { useRustyState } from '../../../state';

/**
 * Wizard-style create page. The shared `IntegrationForm` runs the two-step
 * flow (pick adapter → configure) and reveals the HMAC secret once on success
 * for adapters that opt into HMAC signing.
 */
export function AccountIntegrationCreatePage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const handleSubmit = useCallback(
    async (payload: IntegrationFormSubmitPayload): Promise<SharedIntegration> => {
      const created = await api.newIntegration(payload);
      // The shared `Integration` wire type carries `hmac_secret_b64` directly
      // — backend serializer matches, no field rename needed.
      return {
        id: created.id,
        user_id: created.user_id,
        title: created.title,
        kind: created.kind,
        config: created.config,
        hmac_secret_b64: created.hmac_secret_b64,
        created_at: created.created_at,
        updated_at: created.updated_at,
      };
    },
    [api]
  );

  const handleCancel = useCallback(() => {
    nav(createLanguageURL('/account/integrations'));
  }, [nav, createLanguageURL]);

  return (
    <Container fluid>
      <Stack gap="xl">
        <PageHeader
          title={t('formshive.integrations.createPage.title')}
          subtitle={t('formshive.integrations.createPage.subtitle')}
        />
        <IntegrationForm
          mode="create"
          appScope="formshive"
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Stack>
    </Container>
  );
}
